import React, { useEffect, useState } from "react";
import { useStoreApi, useReactFlow } from 'reactflow';
import moment from "moment";
import { useQuery, useDetail } from "seed/gql";
import * as util from "components/studio/board/Board.lib.util";
import View from "components/studio/board/Board.view";


function Board({
  nodes,
  edges,
  user,
  flowId,
  toolboxRef,
  selectedNodes = [],
  showAnimations,
  isFlowBeingSaved,
  isGeneratingFlow,
  isRPADesigner,
  isDeveloper,
  isAnonymous,
  isAnimationOptionShown,
  isControlsShown = true,
  isValidator,
  reactFlowWrapper,
  setReactFlowInstance,
  onDropNode,
  onDragNode,
  onClickConnectEdge,
  onClickUpdateEdge,
  onNodesChange,
  onEdgesChange,
  onClickNode,
  onClickAnnotation,
  onClickDeleteSelectedNodes,
  onClickDeleteSelectedProcesses,
  onClickUndo,
  onClickRedo,
  onClickChangeAnimations,
  onClickGenerateFlow,
  onClickDuplicateSelectedNodes
}) {

  const store = useStoreApi();
  const { setCenter, getZoom } = useReactFlow();
  const [controlActions, setControlActions] = useState([]);
  const [annotations, setAnnotations] = useState([]);
  const [prevLengthNodes, setPrevLengthNodes] = useState(nodes.length);
  const [isPlaceholderShown, setAnimationOptionShown] = useState(true)
  const numSelectedNodes = selectedNodes.length;

  const reqConditionalAction = useQuery(`{
    actions {
      name
      inputStructure
      outputStructure
      uiSettings
    }
  }`, `name=conditional`, {
    onCompleted: (data) => {
      const actions = data.actions;
      if (actions.length == 0) return;
      const conditionalAction = actions[0];
      setControlActions((prev) => prev.concat(conditionalAction));
    },
    orderBy: "relevance",
    notifyOnNetworkStatusChange: false
  })

  const reqFlow = useDetail(`{
    flow {
      isReleaseVersion
    }
  }`, flowId);

  const reqAnnotations = useQuery(`{
    actions {
      name
      inputStructure
      outputStructure
      uiSettings
    }
  }`, `isAnnotation=true OR name=annotation`, {
    onCompleted: (data) => {
      const annotations = [...data.actions];
      const formattedAnnotations = annotations.map(annotation => {
        const newAnnotation = {...annotation}
        if (annotation.name == "node_annotation") newAnnotation.isAnnotation = false;
        else newAnnotation.isAnnotation = true;
        return newAnnotation;
      })
      setAnnotations((prev) => prev.concat(formattedAnnotations));
    },
    orderBy: "relevance",
    notifyOnNetworkStatusChange: false
  })

  useEffect(() => {
    if (nodes.length > prevLengthNodes) {
      onFocusLastNode()
    }
    setPrevLengthNodes(nodes.length)
  }, [nodes])

  
  const onDragStart = (event, type, actionId) => {
    const data = {
      "type": type,
      "actionId": actionId
    };
    event.dataTransfer.setData('application/reactflow', JSON.stringify(data));
    event.dataTransfer.effectAllowed = 'move';
  };

  const onClickAction = (actionId, type) => {
    if (type == "annotation") {
      onClickAnnotation(actionId)
    } else if (type == "node") {
      const args = {
        actionId: actionId
      }
      onClickNode(args)
    }
  };

  const onFocusLastNode = () => {
    const { nodeInternals } = store.getState();
    const nodes = Array.from(nodeInternals).map(([, node]) => node);

    if (nodes.length > 0) {

      const node = nodes.reduce((maxElem, currentElem) => {
        return moment(currentElem.createdAt) > moment(maxElem.createdAt) ? currentElem : maxElem;
      }, nodes[0])

      const x = node.position.x + 50;
      const y = node.position.y + 25;
      onFocusPoint(x, y);
    }
  }

  const onFocusPoint = (x, y) => {
    const zoom = getZoom();
    setCenter(x, y, { zoom: zoom, duration: 1000 });
  }

  const onClickClosePlaceholder = () =>
    setAnimationOptionShown(false)


  if (reqConditionalAction.loading || reqAnnotations.loading || reqFlow.loading) return <PlaceholderView />;
  if (reqConditionalAction.error || reqAnnotations.error || reqFlow.error) return "Error";

  const { flow = {} } = reqFlow.data;
  const initialBoardRect = util.getInitialBoardRect(nodes);
  const boardFitView = initialBoardRect.width < 600 && initialBoardRect.height < 500;

  const initialBoardZoom = 1.35;
  let initialViewport = { //Only applies when rect.width > 400
    x: -(initialBoardRect.x - 100) * initialBoardZoom,
    y: -(initialBoardRect.y - 70) * initialBoardZoom,
    zoom: initialBoardZoom
  };

  return (
    <View
      flow={flow}
      nodes={nodes}
      edges={edges}
      user={user}
      flowId={flowId}
      toolboxRef={toolboxRef}
      initialViewport={initialViewport}
      boardFitView={boardFitView}
      reactFlowWrapper={reactFlowWrapper}
      annotations={annotations}
      controlActions={controlActions}
      showAnimations={showAnimations}
      numSelectedNodes={numSelectedNodes}
      isFlowBeingSaved={isFlowBeingSaved}
      isGeneratingFlow={isGeneratingFlow}
      isRPADesigner={isRPADesigner}
      isDeveloper={isDeveloper}
      isAnonymous={isAnonymous}
      isAnimationOptionShown={isAnimationOptionShown}
      isPlaceholderShown={isPlaceholderShown}
      isControlsShown={isControlsShown}
      isValidator={isValidator}
      setReactFlowInstance={setReactFlowInstance}
      onDropNode={onDropNode}
      onDragNode={onDragNode}
      onClickConnectEdge={onClickConnectEdge}
      onClickUpdateEdge={onClickUpdateEdge}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onDragStart={onDragStart}
      onClickAction={onClickAction}
      onClickDeleteSelectedNodes={onClickDeleteSelectedNodes}
      onClickDeleteSelectedProcesses={onClickDeleteSelectedProcesses}
      onClickUndo={onClickUndo}
      onClickRedo={onClickRedo}
      onClickClosePlaceholder={onClickClosePlaceholder}
      onClickChangeAnimations={onClickChangeAnimations}
      onClickGenerateFlow={onClickGenerateFlow}
      onClickDuplicateSelectedNodes={onClickDuplicateSelectedNodes}
    />
  );
}

const PlaceholderView = () =>
  <div id="board" className="w-100 h-100">
    <div className="flow-wrapper">
    </div>
  </div>

export default Board;